// Single source of truth for color. Light + dark palettes share the JK gold
// brand primary (#fdbc00) and a full professional supporting palette.
//
// Gold is a LIGHT color, so its contrastText is dark in both modes to keep
// text-on-primary readable (WCAG AA). Dark mode uses elevated greys rather
// than pure black so surfaces read as layered, not flat.

// Brand primary — JK gold, with AA-tuned variants.
const PRIMARY = {
  light: "#ffce4d",
  main: "#fdbc00",
  dark: "#c79100",
  contrastText: "#1a1a1a", // dark text sits on gold for AA contrast
};

// Shared semantic hues (harmonized for both modes).
const SUCCESS = { light: "#48da89", main: "#28c76f", dark: "#1f9d57", contrastText: "#ffffff" };
const INFO = { light: "#1fd5eb", main: "#00cfe8", dark: "#00a7bb", contrastText: "#ffffff" };
const WARNING = { light: "#ffb976", main: "#ff9f43", dark: "#cc7f36", contrastText: "#1a1a1a" };
const ERROR = { light: "#f29291", main: "#ea5455", dark: "#bb4344", contrastText: "#ffffff" };
const SECONDARY = { light: "#4ba0f5", main: "#2186f2", dark: "#1a6bc2", contrastText: "#ffffff" };

// Neutral grey ramp (Vuexy-style).
const GREY = {
  50: "#fafafa",
  100: "#f5f5f5",
  200: "#eeeeee",
  300: "#e0e0e0",
  400: "#bdbdbd",
  500: "#9e9e9e",
  600: "#757575",
  700: "#616161",
  800: "#424242",
  900: "#212121",
};

const lightPalette = {
  mode: "light",
  primary: PRIMARY,
  secondary: SECONDARY,
  success: SUCCESS,
  info: INFO,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  background: {
    default: "#f8f7fa",
    paper: "#ffffff",
  },
  text: {
    primary: "#2f2b3d",
    secondary: "#5b5766",
    disabled: "#a5a3ae",
  },
  divider: "rgba(47, 43, 61, 0.22)",
  action: {
    hover: "rgba(47, 43, 61, 0.04)",
    selected: "rgba(253, 188, 0, 0.12)",
    disabledBackground: "rgba(47, 43, 61, 0.12)",
  },
};

const darkPalette = {
  mode: "dark",
  primary: PRIMARY,
  secondary: SECONDARY,
  success: SUCCESS,
  info: INFO,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  background: {
    default: "#25293c", // elevated dark, not pure black
    paper: "#2f3349",
  },
  text: {
    // Brighter than before so body text stands clear of the dark surfaces.
    primary: "#ecebf0",
    secondary: "#c4c2d1",
    disabled: "#8b8998",
  },
  divider: "rgba(235, 234, 240, 0.24)",
  action: {
    hover: "rgba(219, 218, 222, 0.06)",
    selected: "rgba(253, 188, 0, 0.16)",
    disabledBackground: "rgba(219, 218, 222, 0.12)",
  },
};

/**
 * Returns the palette object for the given color mode.
 * @param {"light"|"dark"} mode
 */
export const getPalette = (mode) => (mode === "dark" ? darkPalette : lightPalette);

export default getPalette;
